import { Component, Element, Host, Prop, State, Watch, h } from '@stencil/core';

import {
  mapContentServiceResultToTeaserList,
  moreContentButtonClassMap,
} from '../../helpers/contentServiceComponent';
import { getAssetSrc, getTenant, renderTeaser } from '../../helpers/helpers';
import {
  ContentTeaserData,
  defaultFetchConfig,
} from '../../helpers/services/ContentTeaserData';
import type {
  ContentServiceResult,
  ContentServiceRubric,
  ContentServiceTag,
  CssClassMap,
  FetchTeaserServiceConfig,
  Rubric,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-content-filter',
  styleUrl: 'cms-content-filter.scss',
  shadow: true,
})
export class CmsContentFilter {
  private readonly filterBgImage =
    getTenant() === 'hornbach' ? 'bg-dark-hb.png' : 'Estrich_dunkel.jpg';
  private cachedAllProjectsResult: ContentServiceResult | undefined;

  private activeKeywordFilter = {
    activeFilterLabel: null,
    activeResultSize: null,
  };

  private loadDataConfig = {
    ...defaultFetchConfig,
    ...{
      locale: null,
      companyCode: null,
      responsePage: 1,
      responseTeaserCount: 18,
      tag: null,
    },
  } as FetchTeaserServiceConfig;

  private imageSizes = `
    (min-width:768px) and (max-width: 1279px) 50vw,
      (min-width:1280px) 368px,
    100vw`;
  private rubricList: Rubric[];

  private filterData: ContentServiceRubric[];

  private defaultRubricList = '';

  @State() teaserResult: ContentServiceResult | undefined;

  /**
   * Set the global Text Label for
   * all projects like"Alle Projekte"
   */
  @Prop() allProjectsLabel: string | undefined;

  /**
   * Set the global Text Label for
   * all projects in Rubrics like"Alle"
   */

  @Prop() allRubricsLabel: string | undefined;

  /**
   * global text that is displayed if the teaser
   * content has more than 220 characters like "Mehr anzeigen"
   */
  @Prop() moreTeaserLabel: string | undefined;

  /**
   * set the user locale like DE_de
   */
  @Prop({ mutable: true }) locale: string | undefined;

  /**
   * set the user companyCode like 1001
   */
  @Prop({ mutable: true }) companyCode: string | undefined;

  /**
   * Set the componenent headline
   * like "Finde Dein Projekt jetzt"
   */
  @Prop() headline: string | undefined;

  /**
   * Set the componenent subheadline
   * like "Durchstöbere die aktuellsten Themen oder wähle einen Filter"
   */
  @Prop() subheadline: string | undefined;

  /**
   * Set the rubrics to collect data for commaseparated
   * like "Garten"
   */

  @Prop() rubrics: Rubric[] | string;

  @Watch('rubrics')
  private rubricsWatcher(newValue: Rubric[] | string) {
    if (newValue) {
      this.rubricList =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as Rubric[])
          : newValue;
    }
  }

  /**
   * Set the Preview Status thrue ore false
   * importent wehen displayed in Content Creator
   * @type {boolean}
   * @memberof CmsContentFilter
   */
  /*  @Prop({ mutable: true }) isPreview: boolean;*/

  /**
   * ref to the host element
   * @type {HTMLElement}
   * @memberof CmsContentFilter
   */
  @Element() host: HTMLElement | undefined;

  /**
   * watch the teaser result from fetch data
   * @private
   * @param {TeaserResult} newState
   * @param {TeaserResult} oldState
   * @memberof CmsContentFilter
   */
  @Watch('teaserResult')
  // @ts-ignore
  /*   private watchStateHandler(newState: TeaserResult, oldState: TeaserResult) {
    this.cachedState.oldState = oldState;
    this.cachedState.newState = newState;
  } */
  /**
   * The method checks if the `newState` parameter is not
   * undefined and if it's different from the current value of
   * the `this.teaserResult` property. If the condition is true,
   * the method sets `this.teaserResult` to the new value.
   * @private
   * @param {TeaserResult} newState
   * @memberof CmsContentFilter
   */
  private setState(newState: ContentServiceResult) {
    if (this.teaserResult !== newState) {
      this.teaserResult = newState;
    }
  }

  private mapRubrics() {
    return this.filterData?.map((rubric: ContentServiceRubric) => {
      return this.renderRubricsHtml(rubric);
    });
  }

  private renderRubricsHtml(rubric: ContentServiceRubric) {
    const { tags, count, name } = rubric;
    return (
      <cms-filter-item class={'filter-item'} filterHeadline={name}>
        <div slot='filter-content' class='filter-content'>
          <div class={'keywords-container'}>
            <ul class={'keyword-list'}>
              <li
                class={this.keywordItemClassMap(name)}
                onClick={(event) => {
                  this.allContentInRubricController(event, rubric);
                  this.closeAllFilterItems();
                }}
              >
                {this.allRubricsLabel} ({count})
              </li>
              {this.mapTags(tags)}
            </ul>
          </div>
        </div>
      </cms-filter-item>
    );
  }

  private mapTags(tagList: ContentServiceTag[]): HTMLElement[] {
    return tagList?.map((tag: ContentServiceTag): HTMLElement => {
      return this.renderTagHtml(tag);
    });
  }

  private allContentInRubricController(
    event: Event,
    rubric: ContentServiceRubric,
  ) {
    const categoryWrapper = (event.target as HTMLElement).offsetParent;
    categoryWrapper.setAttribute('active-filter', this.allProjectsLabel);
    //fixlint this IIFE is only to prevent sonarlint error Promises must be awaited, end with a call to .catch, or end with a call to .then with a rejection handler.
    (async () => {
      await this.loadFilteredContentController(
        'rubric',
        rubric.name,
        rubric.count,
      );
    })();

    this.activeKeywordFilter.activeFilterLabel = `${rubric.name} ${this.allProjectsLabel}`;
    this.activeKeywordFilter.activeResultSize = rubric.count;
    this.renderCurrentRubricLabel();
  }

  private renderTagHtml({ name, count }) {
    return (
      <li
        onClick={() => {
          this.resetResponsePage();
          this.closeAllFilterItems();
          //fixlint this IIFE is only to prevent sonarlint error Promises must be awaited, end with a call to .catch, or end with a call to .then with a rejection handler.
          (async () => {
            await this.loadFilteredContentController('tag', name, count);
          })();
        }}
        class={this.keywordItemClassMap(name)}
      >
        {name} <span class={'count'}>({count})</span>
      </li>
    );
  }

  private resetResponsePage(): void {
    if (this.loadDataConfig.responsePage > 1) {
      this.loadDataConfig.responsePage = 1;
    }
  }

  private closeAllFilterItems(): void {
    const filterItemList =
      this.host.shadowRoot.querySelectorAll('cms-filter-item');
    filterItemList.forEach((item) => {
      const details = item.shadowRoot.querySelector('.filter-details');
      if (details.hasAttribute('open')) {
        item.closeThis = true;
      }
    });
  }

  private renderCurrentRubricLabel() {
    return (
      <div class={'current-category-label-wrapper'}>
        <cms-headline
          style={{
            '--cms-spacing-left-right': '0',
            '--cms-grid-padding': '0',
            '--cms-grid-gap': '0',
          }}
          class={'current-category-label'}
          headlineText={this.activeKeywordFilter.activeFilterLabel ?? null}
          headlineTag={'h3'}
          headlineClass={'current-label'}
          headlinePosition={'left'}
          isAdaptive={true}
        />
        <span class={'current-count-teaser'}>
          ({this.activeKeywordFilter.activeResultSize ?? null})
        </span>
      </div>
    );
  }

  private renderGetAllProjectsButton() {
    return (
      <cms-button
        class={'all-projects-button'}
        onCmsButtonClick={() => this.loadAllProjectsFromState()}
        type='button'
        size='button-lg'
        theme='secondary-inverted'
        label={this.allProjectsLabel ?? null}
      />
    );
  }

  private loadAllProjectsFromState(): void {
    const { activeFilterLabel } = this.activeKeywordFilter;
    if (activeFilterLabel !== this.allProjectsLabel) {
      this.setState(this.cachedAllProjectsResult);
      Object.assign(this.activeKeywordFilter, {
        activeFilterLabel: this.allProjectsLabel,
        activeResultSize: this.teaserResult.caas.total,
      });
    }
  }

  private async loadFilteredContentController(
    filterType: string,
    name: string,
    teaserCount: number,
  ) {
    const { responsePage } = this.loadDataConfig;

    // reset filter for keywords
    this.loadDataConfig.tag = undefined;

    Object.assign(this.loadDataConfig, {
      responsePage: responsePage <= 1 ? 1 : responsePage,
      activeResultSize: this.teaserResult.caas.total,
      [filterType]: name,
    });

    if (filterType === 'tag') {
      Object.assign(this.loadDataConfig, {
        rubric: this.defaultRubricList,
      });
    }

    const keywordTeaserResult: ContentServiceResult =
      await ContentTeaserData.getDataFromService(this.loadDataConfig);
    this.setState(keywordTeaserResult);
    Object.assign(this.activeKeywordFilter, {
      activeFilterLabel: name,
      activeResultSize: teaserCount,
    });
  }

  private async loadMoreContentController() {
    this.loadDataConfig.responsePage++;
    try {
      const moreTeaserResult: ContentServiceResult =
        await ContentTeaserData.getDataFromService(this.loadDataConfig);
      if (this.teaserResult && moreTeaserResult.caas?.documents) {
        moreTeaserResult.caas.documents = [
          ...this.teaserResult.caas.documents,
          ...moreTeaserResult.caas.documents,
        ];
      }
      this.setState(moreTeaserResult);
    } catch (error) {
      console.error(error);
    }
  }

  async componentWillLoad() {
    if (
      this.rubrics === undefined ||
      this.locale === undefined ||
      this.companyCode === undefined
    ) {
      return;
    }

    if (this.rubrics != undefined) {
      this.rubricsWatcher(this.rubrics);
    }

    this.defaultRubricList = this.rubricList?.map((x) => x.name).join(';');

    this.loadDataConfig = {
      ...this.loadDataConfig,
      rubric: this.defaultRubricList,
      locale: this.locale,
      companyCode: this.companyCode,
    };

    this.teaserResult = await ContentTeaserData.getDataFromService(
      this.loadDataConfig,
    );

    const tagsData = this.teaserResult?.caas?.facets?.find(
      (facet) => facet.id === 'f.tags.or',
    ).values;

    this.filterData = this.teaserResult?.caas?.facets
      .find((facet) => facet.id === 'f.rubrics.or')
      .values?.filter((rubric) =>
        this.rubricList?.find((x) => x.name == rubric.value),
      )
      .map((rubric) => {
        const rubricData = this.rubricList.find((x) => x.name == rubric.value);
        if (!rubricData?.tags) {
          return {
            name: rubric.value,
            count: rubric.count,
            tags: [],
          };
        }

        return {
          name: rubric.value,
          count: rubric.count,
          tags: rubricData.tags
            ?.map((tag) => {
              const cleanTag = this.clean(tag);
              const tagData = tagsData.find(
                (x) => this.clean(x.value) === cleanTag,
              );

              // Catch invalid tags - only necessary for Mock-Data
              const count = tagData ? tagData.count : 0;

              return {
                name: tag,
                count,
              };
            })
            .filter((tag) => tag.count > 0)
            .sort((x, y) => x.name.localeCompare(y.name)),
        };
      });

    this.cachedAllProjectsResult = this.teaserResult;

    this.activeKeywordFilter = {
      ...this.activeKeywordFilter,
      activeFilterLabel: this.allProjectsLabel,
      activeResultSize: this.teaserResult.caas.total,
    };
  }

  private clean(tag: string): string {
    return tag.replace(/[‘']/g, '');
  }

  private keywordItemClassMap(name: string): CssClassMap {
    const { activeFilterLabel } = this.activeKeywordFilter;
    return {
      'keyword-list-item': true,
      active: activeFilterLabel === name,
    };
  }

  render() {
    return (
      <Host class={'dynamic-teasers'}>
        <div
          style={{
            backgroundImage: `url(${getAssetSrc(this.filterBgImage)})`,
          }}
          class={'filter-container'}
        >
          <div class={'headline-wrapper '}>
            {this.headline !== undefined ? (
              <cms-headline
                headlineText={this.headline}
                headlineTag={'h2'}
                headlineClass={'content-filter-headline cms-dark-theme'}
                headlinePosition={'center'}
                colorToken={'--cms-semanticColors-base-inverted-strong-text'}
                isAdaptive={true}
              />
            ) : null}
            {this.subheadline !== undefined ? (
              <cms-headline
                headlineText={this.subheadline}
                headlineTag={'h4'}
                headlineClass={'content-filter-subheadline cms-dark-theme'}
                headlinePosition={'center'}
                colorToken={'--cms-semanticColors-base-inverted-strong-text'}
              />
            ) : null}
          </div>

          <div class={'filter-wrapper'}>
            {this.mapRubrics()} {this.renderGetAllProjectsButton()}
          </div>
        </div>

        <div class={'teaser-list-container dynamic-teasers'}>
          {this.renderCurrentRubricLabel()}
          <div class={'teaser-list-container-inner'}>
            {mapContentServiceResultToTeaserList(this.teaserResult)?.map(
              (teaser) =>
                renderTeaser(teaser, this.imageSizes, this.moreTeaserLabel),
            )}
          </div>
        </div>
        <div
          class={moreContentButtonClassMap(
            this.teaserResult,
            this.loadDataConfig,
          )}
        >
          <cms-button
            class={'load-more-teaser'}
            onCmsButtonClick={() => {
              //fixlint this IIFE is only to prevent sonarlint error Promises must be awaited, end with a call to .catch, or end with a call to .then with a rejection handler.
              (async () => {
                await this.loadMoreContentController();
              })();
            }}
            type={'button'}
            size={'button-md'}
            theme={'secondary'}
            label={this.moreTeaserLabel ?? null}
          />
        </div>
      </Host>
    );
  }
}
